import { useEffect, useState } from 'preact/hooks'
import {
  MESH_MAX, STATUS_OPTIONEN, statusFrisch, statusOption, type Presence,
} from '@fh/shared'
import type { UebersetzungAusgang } from '@fh/shared/sprache'

import { anrufUebergeben } from '../anruf/klingeln.js'
import { api, ApiError, type Me } from '../api.js'
import { Avatar } from '../Avatar.js'
import { t } from '../i18n/t.js'
import { ZweisprachigInline } from '../i18n/Zweisprachig.js'
import { anwesenheit, wann } from '../i18n/zeit.js'
import { onEvent } from '../ws.js'

/**
 * Familien-Startseite (Anf. 28, 54, 77; Designregel Anf. 78).
 *
 * Der Bildschirm, an dem sich entscheidet, ob die App bedienbar ist. Drei
 * Regeln, die hier durchgehalten werden:
 *
 *  1. **Eine Person, eine Karte, die Aktionen direkt darauf.** Nicht: Karte
 *     antippen, Profil öffnen, dort ein Menü suchen. Wer Oma anrufen will,
 *     tippt auf „Anrufen" unter Omas Namen (Szenarien 1, 2 und 6 sind damit
 *     zwei Tipps).
 *  2. **Wer Aufmerksamkeit braucht, steht oben.** Die Sortierung kommt vom
 *     Server und richtet sich nach dem Status, nicht nach dem Alphabet.
 *  3. **Keine Zahl ohne Wort.** „3" neben einem Symbol ist eine Rätselaufgabe;
 *     „3 neue Nachrichten" ist eine Information.
 */

interface Mitglied {
  id: string
  displayName: string
  role: string
  presence: Presence
  lastSeenAt: string | null
  statusKey: string | null
  statusText: string | null
  statusAt: string | null
  directRoomId: string | null
  unread: number
  uebersetzung?: UebersetzungAusgang
}

interface DashboardDaten {
  me: {
    id: string; displayName: string
    statusKey: string | null; statusText: string | null; statusAt: string | null
    uebersetzung?: UebersetzungAusgang
  }
  members: Mitglied[]
  unreadTotal: number
  missedCalls: number
  activeCall: { id: string; roomId: string; kind: string } | null
  /**
   * Optional im Typ, obwohl der Server es immer liefert: Bei einem rollenden
   * Update spricht ein frisch geladener Client kurz mit einem alten Server.
   * Eine Startseite, die daran zerbricht, ist schlimmer als eine ohne Verlauf.
   */
  recentStatus?: Array<{
    id: string; personId: string; statusKey: string | null
    statusText: string | null; createdAt: string
    uebersetzung?: UebersetzungAusgang
  }>
}

export interface StartAktionen {
  /** Chat mit dieser Person öffnen — legt den Raum an, falls es keinen gibt. */
  onChat: (personId: string, roomId: string | null) => void
  onAnruf: (personId: string, roomId: string | null, kind: 'audio' | 'video') => void
  onFoto: (personId: string, roomId: string | null) => void
}

export function Start({ me, aktionen }: { me: Me; aktionen: StartAktionen }) {
  const [daten, setDaten] = useState<DashboardDaten | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  async function laden() {
    try {
      // EIN Aufruf für die ganze Seite. Begründung in
      // modules/dashboard/service.ts — auf 3G zählt jede Rückfrage.
      setDaten(await api.get<DashboardDaten>('/dashboard'))
      setFehler(null)
    } catch (e) {
      setFehler(e instanceof ApiError && e.code === 'offline'
        ? t('Keine Verbindung. Du siehst den letzten Stand.')
        : t('Die Übersicht konnte nicht geladen werden.'))
    }
  }

  useEffect(() => { void laden() }, [])

  /**
   * In das laufende Gespräch gehen.
   *
   * Beitreten und den Anruf übergeben — derselbe Weg, den die Annehmen-Kachel
   * und der Anrufknopf auf dieser Seite nehmen. Ohne die Übergabe landet man
   * auf der Anrufliste und sieht dort nur, dass irgendwo ein Anruf läuft.
   */
  async function dazukommen() {
    const call = daten?.activeCall
    if (!call) return
    try {
      anrufUebergeben(await api.post<unknown>(`/calls/${call.id}/join`, {}))
    } catch {
      // Beitreten ging nicht — dann wenigstens hingehen, statt hier zu stehen.
    }
    location.hash = '#anrufen'
  }

  // Status- und Nachrichtenänderungen wirken sofort. Eine Startseite, die den
  // Stand von vor zehn Minuten zeigt, ist schlimmer als eine leere.
  useEffect(() => onEvent((e) => {
    if (['status.changed', 'message.new', 'presence.changed', 'call.ended']
      .includes(e.type)) void laden()
  }), [])

  if (!daten) {
    return (
      <div>
        {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}
        <p class="fh-muted">{t('Wird geladen…')}</p>
      </div>
    )
  }

  return (
    <div class="fh-start">
      {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}

      {daten.activeCall && (
        <p class="fh-hinweis fh-hinweis--info" role="status">
          <span aria-hidden="true">📞</span>
          <span>
            {t('Es läuft gerade ein Anruf.')}{' '}
            {/*
              Ein Knopf, kein Link.

              Hier stand `<a href="#anrufen">`, und das war eine Sackgasse: Der
              Link führte auf die Anrufliste, nicht in das laufende Gespräch.
              Wer ihn tippte, sah dieselbe Liste wie vorher und kam nicht
              hinein — „es läuft ein Anruf" ohne einen Weg dorthin.
            */}
            <button class="fh-btn fh-btn--link" onClick={() => void dazukommen()}>
              {t('Dazukommen')}
            </button>
          </span>
        </p>
      )}

      {daten.missedCalls > 0 && (
        <p class="fh-hinweis fh-hinweis--warnung" role="status">
          <span aria-hidden="true">📵</span>
          <span>
            {daten.missedCalls === 1
              ? t('Ein verpasster Anruf')
              : t('{n} verpasste Anrufe', { n: daten.missedCalls })}
            {' — '}<a href="#anrufen">{t('ansehen')}</a>
          </span>
        </p>
      )}

      {/*
        Die Familie steht OBEN.

        Bis zum Oberflächen-Umbau begann diese Seite mit dem eigenen Status und
        dem Verlauf; die Namen der Familie fingen erst unterhalb des
        Bildschirmrands an. Im Handy-Viewport gemessen: Man musste scrollen, um
        auf einer Seite namens „Familie" die Familie zu sehen. Das ist die
        Anordnung eines Verwaltungswerkzeugs, nicht die eines Adressbuchs.

        Szenario 4 aus Anf. 77 („Mir geht es nicht gut" in höchstens zwei
        Tipps) bleibt erfüllt: Die Statuskacheln liegen weiterhin offen da,
        nur weiter unten. Scrollen ist kein Tipp.
      */}
      <h2 class="fh-h2">{t('Deine Familie')}</h2>

      {daten.members.length === 0 && (
        <p class="fh-muted">
          {t('Noch niemand da. Lade jemanden ein — unter „Mehr" → „Familie verwalten".')}
        </p>
      )}

      <ul class="fh-start__karten">
        {daten.members.map((m) => (
          <Karte key={m.id} mitglied={m} aktionen={aktionen} />
        ))}
      </ul>

      <EigenerStatus
        aktuell={daten.me.statusKey}
        statusAt={daten.me.statusAt}
        onGesetzt={() => void laden()}
      />

      <LetzteMeldungen
        eintraege={daten.recentStatus ?? []}
        namen={Object.fromEntries(daten.members.map((m) => [m.id, m.displayName]))}
        ichId={daten.me.id}
      />

      <p class="fh-muted">{t('Angemeldet als {wer}.', { wer: me.displayName })}</p>
    </div>
  )
}

/**
 * Die letzten Statusmeldungen — der „Verlauf für die Startseite".
 *
 * Eine Liste, keine Auswertung: chronologisch, ungefiltert, ohne
 * Zusammenfassung. Jede Verdichtung („diese Woche 3× müde") wäre eine Aussage
 * über einen Menschen, die niemand getroffen hat — und laut Anf. 10 eine
 * medizinische Schlussfolgerung.
 */
function LetzteMeldungen({ eintraege, namen, ichId }: {
  eintraege: NonNullable<DashboardDaten['recentStatus']>
  namen: Record<string, string>
  ichId: string
}) {
  if (eintraege.length === 0) return null

  return (
    <section class="fh-card">
      <h2 class="fh-h2">{t('Zuletzt gesagt')}</h2>
      <ul class="fh-liste">
        {eintraege.slice(0, 5).map((e) => {
          const option = statusOption(e.statusKey)
          return (
            <li key={e.id} class="fh-verlauf">
              <span class="fh-verlauf__wer">
                {e.personId === ichId ? t('Du') : namen[e.personId] ?? t('Jemand')}
              </span>
              <span class="fh-verlauf__was">
                {option && <><span aria-hidden="true">{option.icon}</span> {t(option.label)}</>}
                {e.statusText && (
                  <>{option && ' — '}<ZweisprachigInline
                    text={e.statusText}
                    uebersetzung={e.uebersetzung?.statusText}
                  /></>
                )}
              </span>
              <span class="fh-verlauf__wann">{wann(e.createdAt)}</span>
            </li>
          )
        })}
      </ul>
      <a class="fh-btn fh-btn--link" href="#status">{t('Alle anzeigen')}</a>
    </section>
  )
}

// --- Eine Person -------------------------------------------------------------

/**
 * Eine Person als Kontaktzeile: runder Avatar, Name, darunter Status oder
 * letzte Aktivität, rechts der Zähler.
 *
 * Bis zum Umbau war das eine Karte in einem Raster — die Form, die man von
 * einem Verwaltungswerkzeug kennt. Eine Zeile mit Avatar ist die Form, die
 * jeder aus seinem Adressbuch kennt, und der Unterschied entscheidet, ob
 * jemand die App beim ersten Öffnen versteht.
 *
 * Die vier Aktionen bleiben offen unter der Zeile. Sie hinter die Zeile zu
 * legen — antippen, Profil, dort ein Menü — wäre der Unterschied zwischen
 * einem Tipp und dreien und würde die Szenarien 1, 2 und 6 aus Anf. 77
 * brechen. `Start.test.tsx` zählt genau das.
 */
function Karte({ mitglied, aktionen }: { mitglied: Mitglied; aktionen: StartAktionen }) {
  const status = statusOption(mitglied.statusKey)
  const frisch = statusFrisch(mitglied.statusAt)
  const auffaellig = status?.auffaellig === true && frisch

  return (
    <li class={auffaellig ? 'fh-karte--achtung' : undefined}>
      <div class="fh-zeile">
        <Avatar name={mitglied.displayName} />

        <span class="fh-zeile__mitte">
          <span class="fh-zeile__name fh-karte__name">{mitglied.displayName}</span>

          {/*
            Die Zweitzeile sagt entweder, wie es der Person geht, oder wann sie
            zuletzt da war — das Wichtigere zuerst. Symbol UND Text: Farbe
            allein trägt die Information nie (Anf. 58).
          */}
          {status && frisch ? (
            <span class="fh-zeile__unter fh-karte__status">
              <span aria-hidden="true">{status.icon}</span> {t(status.label)}
              {mitglied.statusText && (
                <> — <ZweisprachigInline
                  text={mitglied.statusText}
                  uebersetzung={mitglied.uebersetzung?.statusText}
                /></>
              )}
            </span>
          ) : (
            <span class="fh-zeile__unter fh-karte__anwesenheit">
              <span aria-hidden="true">
                {mitglied.presence === 'online' ? '🟢' : '⚪'}
              </span>
              {' '}
              {anwesenheit(mitglied.presence, mitglied.lastSeenAt)}
            </span>
          )}
        </span>

        <span class="fh-zeile__rechts">
          {/*
            Der Zähler sieht aus wie der bekannte grüne Kreis, sagt aber
            vollständig, was er meint — sichtbar die Zahl, vorgelesen der ganze
            Satz. Eine nackte „3" ist eine Rätselaufgabe (Anf. 59); ein Satz an
            dieser Stelle würde die Zeile sprengen.
          */}
          {mitglied.unread > 0 && (
            <span class="fh-zaehler fh-karte__ungelesen">
              {mitglied.unread}
              <span class="fh-visually-hidden">
                {mitglied.unread === 1 ? ` ${t('neue Nachricht')}` : ` ${t('neue Nachrichten')}`}
              </span>
            </span>
          )}
          {/* Anwesenheit auch dann, wenn oben der Status steht. */}
          {status && frisch && (
            <span class="fh-karte__anwesenheit">
              {anwesenheit(mitglied.presence, mitglied.lastSeenAt)}
            </span>
          )}
        </span>
      </div>

      <div class="fh-schnell fh-karte__aktionen">
        <button
          class="fh-btn"
          onClick={() => aktionen.onChat(mitglied.id, mitglied.directRoomId)}
        >
          <span aria-hidden="true">💬</span> {t('Nachricht')}
        </button>
        <button
          class="fh-btn"
          onClick={() => aktionen.onAnruf(mitglied.id, mitglied.directRoomId, 'audio')}
        >
          <span aria-hidden="true">📞</span> {t('Anrufen')}
        </button>
        <button
          class="fh-btn"
          onClick={() => aktionen.onAnruf(mitglied.id, mitglied.directRoomId, 'video')}
        >
          <span aria-hidden="true">📹</span> {t('Video')}
        </button>
        <button
          class="fh-btn"
          onClick={() => aktionen.onFoto(mitglied.id, mitglied.directRoomId)}
        >
          <span aria-hidden="true">📷</span> {t('Foto')}
        </button>
      </div>
    </li>
  )
}

// --- Eigener Status ----------------------------------------------------------

function EigenerStatus({ aktuell, statusAt, onGesetzt }: {
  aktuell: string | null
  statusAt: string | null
  onGesetzt: () => void
}) {
  const [offen, setOffen] = useState(false)
  const [busy, setBusy] = useState(false)
  const gesetzt = statusOption(aktuell)
  const frisch = statusFrisch(statusAt)

  async function setzen(key: string | null) {
    setBusy(true)
    try {
      await api.put('/me/status', { statusKey: key })
      setOffen(false)
      onGesetzt()
    } finally {
      setBusy(false)
    }
  }

  return (
    <section class="fh-eigenerstatus">
      <h2 class="fh-h2">{t('Wie geht es dir?')}</h2>

      {gesetzt && frisch ? (
        <p class="fh-eigenerstatus__aktuell">
          <span aria-hidden="true">{gesetzt.icon}</span> {t(gesetzt.label)}
          {' '}
          <button
            class="fh-btn fh-btn--link"
            disabled={busy}
            onClick={() => void setzen(null)}
          >
            {t('Zurücknehmen')}
          </button>
        </p>
      ) : (
        <p class="fh-muted">{t('Sag deiner Familie kurz Bescheid — mit einem Tipp.')}</p>
      )}

      {/*
        Aufgeklappt statt hinter einem Menü: Szenario 4 verlangt „Mir geht es
        nicht gut" in höchstens zwei Tipps. Ein Aufklapper wäre schon einer
        davon — deshalb sind die Kacheln direkt da, sobald man auf der
        Startseite ist.
      */}
      <div class="fh-eigenerstatus__kacheln" hidden={offen ? false : undefined}>
        {STATUS_OPTIONEN.map((o) => (
          <button
            key={o.key}
            class={`fh-btn fh-statuskachel${o.key === aktuell ? ' fh-statuskachel--an' : ''}`}
            disabled={busy}
            aria-pressed={o.key === aktuell}
            onClick={() => void setzen(o.key)}
          >
            <span aria-hidden="true">{o.icon}</span>
            <span>{t(o.label)}</span>
          </button>
        ))}
      </div>
      {/* `offen` steuert nur die kompakte Darstellung auf sehr kleinen Geräten. */}
      <button class="fh-btn fh-btn--link fh-nur-schmal" onClick={() => setOffen(!offen)}>
        {offen ? t('Weniger') : t('Alle Möglichkeiten')}
      </button>

      {/* Eigene Worte gehen im Status-Bereich — hier wäre ein Textfeld zu viel. */}
      <a class="fh-btn fh-btn--link" href="#status">{t('Lieber in eigenen Worten?')}</a>
    </section>
  )
}

/** Die Mesh-Grenze als Hinweis, wo sie hingehört: bevor jemand darauf stößt. */
export const VIDEO_HINWEIS = `Video geht mit bis zu ${MESH_MAX} Personen.`
